"use client";

import { Cloud, CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import type { SyncState } from "@/core/offline/sync-engine";

interface SyncIndicatorProps {
  syncState: SyncState;
  pendingCount: number;
  /** Entités bloquées (error > cap retries C5) : ne se re-poussent plus seules. */
  stuckCount?: number;
  isStale: boolean;
  onForceSync: () => void;
}

/**
 * Indicateur de synchronisation dans le header.
 * - Nuage = syncé
 * - Nuage barré = offline
 * - Flèches tournantes = sync en cours
 * - Triangle = erreur, données stale, ou entités bloquées
 */
export function SyncIndicator({
  syncState,
  pendingCount,
  stuckCount = 0,
  isStale,
  onForceSync,
}: SyncIndicatorProps) {
  const showBadge = pendingCount > 0 && syncState !== "syncing";
  // Entités bloquées = alerte prioritaire (elles ne partiront pas sans action).
  const hasStuck = stuckCount > 0;

  return (
    <button
      onClick={onForceSync}
      className="relative flex items-center justify-center size-8 rounded-md transition-colors hover:bg-muted"
      title={getTitle(syncState, pendingCount, isStale, stuckCount)}
      disabled={syncState === "syncing" || syncState === "offline"}
    >
      {syncState === "offline" && (
        <CloudOff className="size-4 text-muted-foreground" />
      )}

      {syncState === "syncing" && (
        <RefreshCw className="size-4 text-primary animate-spin" />
      )}

      {/* Entités bloquées : alerte ambre prioritaire même à l'état idle/synced. */}
      {syncState === "idle" && hasStuck && (
        <AlertTriangle className="size-4 text-amber-500" />
      )}

      {syncState === "idle" && !hasStuck && !isStale && (
        <Cloud className="size-4 text-emerald-500" />
      )}

      {syncState === "idle" && !hasStuck && isStale && (
        <AlertTriangle className="size-4 text-amber-500" />
      )}

      {syncState === "error" && (
        <AlertTriangle className="size-4 text-destructive" />
      )}

      {/* Badge compteur : ambre si des entités sont bloquées, sinon couleur primaire. */}
      {showBadge && (
        <span
          className={`absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full text-2xs font-bold ${
            hasStuck
              ? "bg-amber-500 text-white"
              : "bg-primary text-primary-foreground"
          }`}
        >
          {pendingCount > 9 ? "9+" : pendingCount}
        </span>
      )}
    </button>
  );
}

function getTitle(
  syncState: SyncState,
  pendingCount: number,
  isStale: boolean,
  stuckCount: number
): string {
  const stuckSuffix =
    stuckCount > 0
      ? ` — ⚠ ${stuckCount} bloquée(s) (échecs répétés, action requise)`
      : "";
  switch (syncState) {
    case "offline":
      return "Hors ligne";
    case "syncing":
      return "Synchronisation en cours...";
    case "error":
      return `Erreur de synchronisation (${pendingCount} en attente)${stuckSuffix}`;
    case "idle":
      if (stuckCount > 0)
        return `${stuckCount} modification(s) bloquée(s) après échecs répétés — cliquez pour réessayer`;
      if (isStale) return `Données non syncées depuis plus de 48h (${pendingCount} en attente)`;
      if (pendingCount > 0) return `${pendingCount} modification(s) en attente — cliquez pour synchroniser`;
      return "Synchronisé";
  }
}
